/**
 * useNotifications — Author notification hooks.
 *
 * For authors: query their notifications, mark as read.
 * For admins: send notifications to authors.
 */
import { useCallback } from 'react';
import { supabase } from '@/core/supabase';
import { useSupabaseQuery, type QueryFn } from '@/shared/hooks/useSupabaseQuery';
import { useAuthStore } from '@/shared/stores/authStore';
import type { AuthorNotificationRow, NotificationType } from '@/core/supabase/database.types';

/** Query current user's notifications (newest first) */
export function useMyNotifications(limit = 50) {
  const user = useAuthStore((s) => s.user);
  const queryFn: QueryFn<AuthorNotificationRow[]> = useCallback(() => {
    return supabase
      .from('author_notifications')
      .select('*')
      .eq('user_id', user?.id ?? '')
      .order('created_at', { ascending: false })
      .limit(limit);
  }, [user?.id, limit]);

  return useSupabaseQuery<AuthorNotificationRow[]>(queryFn, ['notifications', user?.id, limit], { enabled: !!user?.id });
}

/** Count unread notifications */
export function useUnreadCount() {
  const user = useAuthStore((s) => s.user);
  const queryFn = useCallback(() => {
    return supabase
      .from('author_notifications')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', user?.id ?? '')
      .eq('is_read', false)
      .then((res: { count: number | null; error: { message: string } | null }) => ({
        data: (res.count ?? 0) as unknown as number,
        error: res.error ? { message: res.error.message } : null,
      }));
  }, [user?.id]);

  return useSupabaseQuery<number>(queryFn as QueryFn<number>, ['unread-count', user?.id], { enabled: !!user?.id });
}

/** Mark a notification as read */
export async function markNotificationRead(notificationId: string) {
  return supabase
    .from('author_notifications')
    .update({ is_read: true })
    .eq('id', notificationId);
}

/** Mark all notifications as read */
export async function markAllNotificationsRead(userId: string) {
  return supabase
    .from('author_notifications')
    .update({ is_read: true })
    .eq('user_id', userId)
    .eq('is_read', false);
}

/** Send a notification to an author (admin use) */
export async function sendAuthorNotification(args: {
  userId: string;
  type: NotificationType;
  title: string;
  content?: string;
  relatedNovelId?: string;
  relatedReviewId?: string;
}) {
  return supabase
    .from('author_notifications')
    .insert({
      user_id: args.userId,
      type: args.type,
      title: args.title,
      content: args.content ?? '',
      related_novel_id: args.relatedNovelId ?? null,
      related_review_id: args.relatedReviewId ?? null,
    });
}